import config from 'config'
import {
  getMarket,
  getMpvid,
  stringRenderPropertySelector,
  booleanRenderPropertySelector,
  getLocaleSelector,
  getQuantitySelector,
  selectedOptionsSelector,
  getProductKey,
} from '~/app/store/config'
import { isPpagGallerySelector } from '~/app/store/config/reducer'
import { getBypassApproval, getNoCache, getUseConstraints } from '~/app/store/debug'
import { QUICKVIEW_FILTER_TYPE, REFINEMENT_DIMENSION, SCENE_PURPOSE, SCENE_SOUCE } from '~/shared/constants'
import { createSelector } from 'reselect'
import { filterByIdSelector, selectFilters } from '../filter'
import { filterOptionByIdSelector } from '../filterOption'
import { quickViewFilterByIdSelector, selectQuickViewFilterNames } from '../quickViewFilter'
import { getDebugModeEnabled, getSearchBackend } from '../debug/reducer'
import { categoryRefinementsSelector } from '../refinement/selectors'
import { designPersonalizationContextIdSelector, customColorVariationsSelector } from '../designPersonalization/selectors'
import { effectiveDesignCreationTypesSelector } from '../designCreationTypes/selectors'
import type { DesignVariations } from 'shared/types/gallery/design-variations'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { State } from 'shared/types/gallery/state'
import { RenderProperty } from 'shared/renderProperties'

const {
  client: { features },
} = config

export const globalQuickViewDataSelector = createSelector(
  getMarket,
  getMpvid,
  getLocaleSelector,
  getQuantitySelector,
  getBypassApproval,
  (market, mpvId, locale, quantity, bypassedApproval) => ({
    market,
    mpvId,
    locale,
    quantity,
    bypassedApproval,
  }),
)

export const quickViewControlFlagsSelector = createSelector(
  stringRenderPropertySelector,
  booleanRenderPropertySelector,
  (stringRenderProperty, booleanRenderProperty) => {
    const pricingPresentationType = stringRenderProperty(RenderProperty.PricingPresentationType)
    const shouldRenderVortex = features.EnableVortex && booleanRenderProperty(RenderProperty.VortexEnabled)
    const shouldRenderIconography = features.EnableIconography && booleanRenderProperty(RenderProperty.IconographyEnabled)
    const showComparativeNames = booleanRenderProperty(RenderProperty.ShowComparativeNames)

    return {
      pricingPresentationType,
      shouldRenderVortex,
      showComparativeNames,
      shouldRenderIconography,
    }
  },
)

export const designVariationsInputSelector = createSelector(
  getProductKey,
  getMpvid,
  getLocaleSelector,
  getSearchBackend,
  getNoCache,
  getUseConstraints,
  selectQuickViewFilterNames,
  selectFilters,
  selectedOptionsSelector,
  effectiveDesignCreationTypesSelector,
  categoryRefinementsSelector,
  getDebugModeEnabled,
  filterOptionByIdSelector,
  stringRenderPropertySelector,
  quickViewFilterByIdSelector,
  filterByIdSelector,
  isPpagGallerySelector,
  designPersonalizationContextIdSelector,
  customColorVariationsSelector,
  (
    productKey,
    mpvId,
    locale,
    searchBackend,
    noCache,
    useConstraints,
    quickviewFilterNames,
    allFilters,
    selectedOptionsFromUrl,
    designCreationTypes,
    categoryRefinements,
    debugEnabled,
    getOption,
    stringRenderProperty,
    quickViewFilterById,
    filterById,
    isPpagGallery,
    dpcId,
    customColorVariations,
  ) =>
    (
      tileEntity: State.TileEntity,
      selectedPartialProductOptions: GalleryContentQuery.ProductOptions,
      selectedTaxonomyIds?: string[],
    ): DesignVariations.DesignVariationsInput => {
      const sceneSource = stringRenderProperty(RenderProperty.SceneSource) ?? SCENE_SOUCE.SCENE_LOOKUP_SOURCED
      const quickviewFilters = quickviewFilterNames.map((f) => filterById(f))
      const quickviewFilterMetadata = quickviewFilterNames.map((f) => quickViewFilterById(f))
      const filters = allFilters.map((f) => filterById(f.name))

      const selectedOptions = {} as GalleryContentQuery.ProductOptions
      let variableOptions = [] as string[]

      Object.entries(selectedPartialProductOptions).forEach(([productOptionName, productOptionValue]) => {
        const quickViewFilterExists = quickviewFilters
          .filter((x) => x.dimension !== REFINEMENT_DIMENSION.CATEGORY)
          .some((f) => {
            const option = getOption((f as State.AttributeFilter).options[0])

            return option.productOption?.optionName === productOptionName
          })
        const filterExists = filters.some((f) => {
          if (!('options' in f)) {
            return false
          }

          const option = getOption((f as State.AttributeFilter).options[0])

          return option.productOption?.optionName === productOptionName
        })

        if (quickViewFilterExists) {
          variableOptions = variableOptions.concat(productOptionName)
        } else if (selectedOptionsFromUrl[productOptionName] || filterExists) {
          selectedOptions[productOptionName] = productOptionValue
        }
      })

      const variableAttributes = quickviewFilterMetadata
        .filter((f) => f.quickViewFilterType === QUICKVIEW_FILTER_TYPE.ATTRIBUTE)
        .filter((f) => {
          const filter = filterById(f.name) as State.AttributeFilter
          const option = getOption(filter.options[0])

          return !option.productOption
        })
        .map((f) => {
          const filter = filterById(f.name) as State.AttributeFilter
          const option = getOption(filter.options[0])

          return option.value.split('_')[0]
        })

      const variableTaxonomyIds = quickviewFilterMetadata
        .filter(
          (f) =>
            f.quickViewFilterType === QUICKVIEW_FILTER_TYPE.RADIO && selectedTaxonomyIds?.includes((filterById(f.name) as State.CategoryFilter).taxonomyId),
        )
        .map((f) => (filterById(f.name) as State.CategoryFilter).taxonomyId)

      return {
        templateToken: tileEntity.previewInfo.templateToken,
        designCreationTypes,
        locale,
        selectedOptions,
        variableOptions,
        variableAttributes,
        productKey,
        mpvId,
        noCache,
        useConstraints,
        sceneSource,
        searchBackend,
        taxonomyIds: categoryRefinements.map((r) => r.value),
        variableTaxonomyIds,
        debug: debugEnabled,
        designPersonalizationContextId: dpcId,
        customColorVariations,
        ...(isPpagGallery && { scenePurpose: SCENE_PURPOSE.DESIGN_STUDIO_REVIEW }),
      }
    },
)
